import { useEffect, useState } from 'react';
import windowService from '../../../services/windowService';
import { logger } from '../../../utils/logger';
import { getUserOrgs, Org, useOrg } from '../../org';
import { getPathway } from '../api/getPathway';
import { AllowableOrg, toAllowableOrg } from '../models/allowableOrg';
import { useSwitchOrg } from './useSwitchOrg';
import { useUser } from '../../authentication';
import { getTrainingOverview } from '../api/getTrainingOverview';
import { ActivePathwayOverview, Assignment } from '../models/assignment';

type PartialTraining = {
  id: string;
  name: string;
  posterImage?: string;
  description?: string;
};

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'verifyOrg';
      training: PartialTraining;
      currentOrg: Org;
      allowableOrgs: AllowableOrg[];
      isRestricted?: boolean;
    }
  | { status: 'selectOrg'; training: PartialTraining; currentOrg: Org; allowableOrgs: AllowableOrg[] }
  | { status: 'noOrg'; training: PartialTraining }
  | { status: 'trainingExists'; training: PartialTraining; trainingInstance: ActivePathwayOverview; currentOrg: Org };

type UseRequireOrgForTrainingResult = {
  state: State;
  onChangeChurch: () => void;
  onOrgSelected: (orgId: string) => void;
  onSelectOrgBack: () => void;
  onCreateChurchAccount: () => void;
};

const usePageState = () => {
  const [state, setState] = useState<State>({ status: 'loading' });

  const actions = {
    setLoading: () => setState({ status: 'loading' }),
    setError: (message: string) => setState({ status: 'error', message }),
    setVerifyOrg: (training: PartialTraining, currentOrg: Org, allowableOrgs: AllowableOrg[]) => {
      const isRestricted = !allowableOrgs.some(o => o.id === currentOrg.id);
      setState({ status: 'verifyOrg', training, currentOrg, allowableOrgs, isRestricted });
    },
    setNoOrg: (training: PartialTraining) => setState({ status: 'noOrg', training }),
    setSelectOrg: (training: PartialTraining, currentOrg: Org, allowableOrgs: AllowableOrg[]) =>
      setState({ status: 'selectOrg', training, currentOrg, allowableOrgs }),
    setExistingTraining: (training: PartialTraining, currentOrg: Org, trainingInstance: ActivePathwayOverview) =>
      setState({ status: 'trainingExists', training, currentOrg, trainingInstance }),
  };

  return [state, actions] as const;
};

export const useRequireOrgForTraining = (trainingId: string): UseRequireOrgForTrainingResult => {
  const { org, isLoading: isOrgLoading } = useOrg();
  const { user } = useUser();
  const [fetchedData, setFetchedData] = useState<{
    training: PartialTraining;
    allowableOrgs: AllowableOrg[];
    trainingInstance?: Assignment;
  }>();
  const { switchToOrg } = useSwitchOrg();
  const [state, { setLoading, setError, setVerifyOrg, setNoOrg, setSelectOrg, setExistingTraining }] = usePageState();

  useEffect(() => {
    if (isOrgLoading || !fetchedData) return;

    const { training, allowableOrgs, trainingInstance } = fetchedData;
    if (trainingInstance && trainingInstance._type === 'ActiveWorkflowOverview') {
      switchToOrg(trainingInstance.assignorId.id)
        .then(() => {
          if (org) {
            setExistingTraining(training, org, trainingInstance);
          }
        })
        .catch(error => {
          logger.error('Failed to switch org', error);
          setError('Unable to switch church. Please try again.');
        });
    } else if (org) {
      setVerifyOrg(training, org, allowableOrgs);
    } else {
      setNoOrg(training);
    }
  }, [org?.id, isOrgLoading, fetchedData]);

  useEffect(() => {
    if (!user) {
      return;
    }

    // TODO: handle courses
    // Right now, we only need this for specific pathways, but when we begin to use this
    // for courses, we'll need to figure out a way to determine the training type.
    // Since we need limited data, the best approach would be to add the
    // posterImage url to the search request which take either a pathway or course id.
    Promise.all([getPathway(trainingId), getUserOrgs(true), getTrainingOverview(user.userId, false)])
      .then(([fetchedTraining, userOrgs, currentTrainingList]) => {
        const training: PartialTraining = {
          id: fetchedTraining._id,
          name: fetchedTraining.name,
          posterImage: fetchedTraining.posterImage,
          description: fetchedTraining.description,
        };

        const allowableOrgs = userOrgs.map(toAllowableOrg);
        const existingTraining = currentTrainingList.find(
          (t: Assignment) => 'originalTrainingId' in t && t.originalTrainingId === trainingId
        );
        const trainingInstance = existingTraining ? existingTraining : undefined;
        setFetchedData({ training, allowableOrgs, trainingInstance });
      })
      .catch(error => {
        logger.error('Failed to load training or allowable orgs', error);
        setError('Failed to load necessary information. Please try again.');
      });
  }, [trainingId, user?.userId]);

  const onChangeChurch = () => {
    if (state.status !== 'verifyOrg') return;
    setSelectOrg(state.training, state.currentOrg, state.allowableOrgs);
  };

  const onCreateChurchAccount = () => {
    windowService.redirectTo(`/org/create?redirect=/training/begin/${trainingId}`);
  };

  const onOrgSelected = (orgId: string) => {
    if (state.status !== 'selectOrg') return;

    const selectedOrg = state.allowableOrgs.find(o => o.id === orgId);
    if (!selectedOrg) return;

    if (orgId === org?.id) {
      setVerifyOrg(state.training, state.currentOrg, state.allowableOrgs);
      return;
    }

    setLoading();

    switchToOrg(orgId).catch(error => {
      logger.error('Failed to switch org', error);
      setError('Unable to switch church. Please try again.');
    });
  };

  const onSelectOrgBack = () => {
    if (state.status !== 'selectOrg') return;
    setVerifyOrg(state.training, state.currentOrg, state.allowableOrgs);
  };

  return {
    state,
    onChangeChurch,
    onCreateChurchAccount,
    onOrgSelected,
    onSelectOrgBack,
  };
};
